// Engine Service - GraphQL operations for Engine role
import { graphqlClient } from '../graphqlClient';

// ==================== Profile ====================

export const getProfile = async () => {
  const query = `
    query GetEngineProfile {
      engineProfile {
        id
        email
        name
        status
        code
        contactName
        contactPhone
        supportEmail
        notes
        apiKey
        secretKey
        depositCommission
        withdrawalCommission
        percentageFee
        parentName
        parentType
        parentDepositCommission
        parentWithdrawalCommission
        parentPercentageFee
        createdAt
        updatedAt
      }
    }
  `;

  const result = await graphqlClient.query(query);
  return result.engineProfile;
};

export const updateProfile = async (input) => {
  const mutation = `
    mutation EngineUpdateProfile($input: EngineUpdateProfileInput!) {
      engineUpdateProfile(input: $input) {
        id
        email
        name
        status
        code
        contactName
        contactPhone
        supportEmail
        notes
        apiKey
        secretKey
        depositCommission
        withdrawalCommission
        percentageFee
        parentName
        parentType
        parentDepositCommission
        parentWithdrawalCommission
        parentPercentageFee
        createdAt
        updatedAt
      }
    }
  `;

  const result = await graphqlClient.mutate(mutation, { input });
  return result.engineUpdateProfile;
};

export const regenerateApiKeys = async () => {
  const mutation = `
    mutation EngineRegenerateApiKeys {
      engineRegenerateApiKeys {
        id
        apiKey
        secretKey
        updatedAt
      }
    }
  `;

  const result = await graphqlClient.mutate(mutation);
  return result.engineRegenerateApiKeys;
};

// ==================== Reseller Operations ====================

export const getMyResellers = async (page = 1, limit = 10, search = "") => {
  const query = `
    query GetEngineResellers($page: Int, $limit: Int, $search: String) {
      engineResellers(page: $page, limit: $limit, search: $search) {
        data {
          id
          code
          email
          name
          status
          createdByAdminId
          createdByEngineId
          depositFee
          withdrawalFee
          contactName
          contactPhone
          supportEmail
          notes
          maxMerchants
          allowedProviders
          routingOverride
          apiKey
          secretKey
          merchantsCount
          createdByAdminId
          createdByEngineId
          createdAt
          updatedAt
        }
        total
        totalPages
        page
        limit
      }
    }
  `;

  const result = await graphqlClient.query(query, { page, limit, search });
  return result.engineResellers;
};

export const getResellerById = async (id) => {
  const query = `
    query GetEngineResellerById($id: String!) {
      engineResellerById(id: $id) {
        id
        code
        email
        name
        status
        depositFee
        withdrawalFee
        contactName
        contactPhone
        supportEmail
        notes
        maxMerchants
        allowedProviders
        routingOverride
        apiKey
        secretKey
        merchantsCount
        createdAt
        updatedAt
      }
    }
  `;

  const result = await graphqlClient.query(query, { id });
  return result.engineResellerById;
};

export const createReseller = async (input) => {
  const mutation = `
    mutation EngineCreateReseller($input: EngineCreateResellerInput!) {
      engineCreateReseller(input: $input) {
        id
        code
        email
        name
        status
        depositFee
        withdrawalFee
        contactName
        contactPhone
        supportEmail
        notes
        maxMerchants
        allowedProviders
        routingOverride
        apiKey
        secretKey
        createdAt
        updatedAt
      }
    }
  `;

  const result = await graphqlClient.mutate(mutation, { input });
  return result.engineCreateReseller;
};

export const updateReseller = async (id, input) => {
  const mutation = `
    mutation EngineUpdateReseller($id: String!, $input: EngineUpdateResellerInput!) {
      engineUpdateReseller(id: $id, input: $input) {
        id
        code
        email
        name
        status
        depositFee
        withdrawalFee
        contactName
        contactPhone
        supportEmail
        notes
        maxMerchants
        allowedProviders
        routingOverride
        apiKey
        secretKey
        createdAt
        updatedAt
      }
    }
  `;

  const result = await graphqlClient.mutate(mutation, { id, input });
  return result.engineUpdateReseller;
};

export const deleteReseller = async (id) => {
  const mutation = `
    mutation EngineDeleteReseller($id: String!) {
      engineDeleteReseller(id: $id)
    }
  `;

  const result = await graphqlClient.mutate(mutation, { id });
  return result.engineDeleteReseller;
};

// ==================== Merchant Operations ====================

export const getMyMerchants = async (page = 1, limit = 10, search = "") => {
  const query = `
    query GetEngineMerchants($page: Int, $limit: Int, $search: String) {
      engineMerchants(page: $page, limit: $limit, search: $search) {
        data {
          id
          code
          email
          name
          status
          apiKey
          secretKey
          minDeposit
          maxDeposit
          minWithdrawal
          maxWithdrawal
          dailyDepositLimit
          dailyWithdrawalLimit
          depositFee
          withdrawalFee
          settlementPeriod
          callbackUrl
          webhookUrl
          notes
          contactName
          contactEmail
          contactPhone
          depositEnabled
          withdrawalEnabled
          ipWhitelist
          rateLimit
          createdByAdminId
          createdByEngineId
          createdByResellerId
          createdAt
        }
        total
        totalPages
        page
        limit
        totalActiveMerchants
        totalRegisteredMerchants
      }
    }
  `;
  const result = await graphqlClient.query(query, { page, limit, search });
  return result.engineMerchants;
};

export const getMerchantById = async (id) => {
  const query = `
    query GetEngineMerchantById($id: String!) {
      engineMerchantById(id: $id) {
        id
        code
        email
        name
        status
        apiKey
        secretKey
        minDeposit
        maxDeposit
        minWithdrawal
        maxWithdrawal
        dailyDepositLimit
        dailyWithdrawalLimit
        depositFee
        withdrawalFee
        settlementPeriod
        callbackUrl
        webhookUrl
        ipWhitelist
        rateLimit
        depositEnabled
        withdrawalEnabled
        notes
        createdAt
        updatedAt
      }
    }
  `;

  const result = await graphqlClient.query(query, { id });
  return result.engineMerchantById;
};

export const createMerchant = async (input) => {
  const mutation = `
    mutation EngineCreateMerchant($input: EngineCreateMerchantInput!) {
      engineCreateMerchant(input: $input) {
        id
        code
        email
        name
        status
        createdAt
        updatedAt
      }
    }
  `;

  const result = await graphqlClient.mutate(mutation, { input });
  return result.engineCreateMerchant;
};

export const updateMerchant = async (id, input) => {
  const mutation = `
    mutation EngineUpdateMerchant($id: String!, $input: EngineUpdateMerchantInput!) {
      engineUpdateMerchant(id: $id, input: $input) {
        id
        code
        email
        name
        status
        createdAt
        updatedAt
      }
    }
  `;

  const result = await graphqlClient.mutate(mutation, { id, input });
  return result.engineUpdateMerchant;
};

export const deleteMerchant = async (id) => {
  const mutation = `
    mutation EngineDeleteMerchant($id: String!) {
      engineDeleteMerchant(id: $id)
    }
  `;

  const result = await graphqlClient.mutate(mutation, { id });
  return result.engineDeleteMerchant;
};

// ==================== Dashboard Stats ====================

export const getDashboardStats = async () => {
  const query = `
    query GetEngineDashboardStats {
      engineDashboardStats {
        totalResellers
        totalMerchants
        activeResellers
        activeMerchants
      }
    }
  `;

  const result = await graphqlClient.query(query);
  return result.engineDashboardStats;
};


// ==================== Get webhook logs ====================

export const getWebhookLogs = async ({ merchantIds, search, page, limit }) => {
  const query = `
    query GetWebhookLogs($merchantIds: [String!]!, $search: String, $page: Float, $limit: Float) {
      webhookLogs(merchantIds: $merchantIds, search: $search, page: $page, limit: $limit) {
        data {
          id
          qr_code_id
          payment_id
          merchant_id
          event
          url
          http_status
          response_body
          error_message
          request_payload
          created_at
        }
        total
        page
        limit
        totalPages
      }
    }
  `;

  const result = await graphqlClient.query(query, {
    merchantIds,
    search,
    page,
    limit
  });

  return result.webhookLogs;
};
